// @ts-check
// Formulário "Novo modelo" (contrato 08 de leads e WhatsApp, seção 7): número da conta (com mais de um), nome,
// categoria, cabeçalho sem variáveis, corpo com "Inserir variável", um exemplo por variável, rodapé e a prévia ao vivo
// com os exemplos (ao lado no computador, abaixo no celular). O modelo vai para aprovação da Meta.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { FormError, SelectField, TextField, describeError } from '../integration-ui.js';
import { createWhatsappTemplate } from '../../../data/integrations.js';
import { Modal } from '../../../ui/modal.js';
import { Button } from '../../../ui/button.js';
import { enqueueSnackbar } from '../../../ui/snackbar.js';
import { channelLabel } from '../../../lib/whatsapp-templates.js';
import { TemplatePreviewBubble, languageLabel } from './whatsapp-template-card.js';
import { t } from '../../../i18n/index.js';

const VARIABLE_PATTERN = /\{\{\s*(\w+)\s*\}\}/g;
const TEMPLATE_LANGUAGE = 'pt_BR';

/** @param {string} text */
const variablesOf = (text) => [...new Set([...text.matchAll(VARIABLE_PATTERN)].map((match) => match[1]))];

/**
 * Próxima variável numerada do corpo ({{1}}, {{2}}...).
 * @param {string} bodyText
 */
export function nextVariableToken(bodyText) {
  const numbers = variablesOf(bodyText).filter((name) => /^\d+$/.test(name)).map(Number);
  return `{{${(numbers.length > 0 ? Math.max(...numbers) : 0) + 1}}}`;
}

/**
 * @param {{ channels: import('../../../data/conversations.js').Channel[], initialChannelId?: string | null, onClose: () => void,
 *   onCreated: (template: import('../../../data/integrations.js').WhatsappTemplate) => void,
 *   successAction?: { label: string, onClick: () => void } | null }} props
 *   successAction: botão no aviso de criado (o "Voltar para onde você estava" de quem veio por um atalho)
 */
export function CreateTemplateModal({ channels, initialChannelId = null, onClose, onCreated, successAction = null }) {
  const [channelId, setChannelId] = useState(() => (channels.some((channel) => channel.id === initialChannelId) ? /** @type {string} */ (initialChannelId) : channels[0]?.id ?? ''));
  const [name, setName] = useState('');
  const [category, setCategory] = useState('UTILITY');
  const [headerText, setHeaderText] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [examples, setExamples] = useState(/** @type {Record<string, string>} */ ({}));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const variables = variablesOf(bodyText);
  const headerHasVariable = variablesOf(headerText).length > 0;
  const missingExample = variables.some((variable) => !String(examples[variable] ?? '').trim());

  const submit = async () => {
    if (headerHasVariable) return;
    setIsSaving(true);
    setError(null);
    try {
      const template = await createWhatsappTemplate({
        channelId, name: name.trim(), language: TEMPLATE_LANGUAGE, category, headerText: headerText.trim(), bodyText: bodyText.trim(), footerText: footerText.trim(),
        examples: variables.map((variable) => examples[variable] ?? ''),
      });
      enqueueSnackbar({
        message: t('whatsappTemplates.form.created'), variant: 'success',
        ...(successAction ? { buttonLabel: successAction.label, onButtonClick: successAction.onClick } : {}),
      });
      onCreated(template);
    } catch (requestError) {
      setError(describeError(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  return html`<${Modal} title=${t('whatsappTemplates.form.title')} subtitle=${t('whatsappTemplates.form.subtitle')} confirmLabel=${t('whatsappTemplates.form.submit')}
    isConfirmDisabled=${!channelId || !name.trim() || !bodyText.trim() || headerHasVariable || missingExample} isLoading=${isSaving} onClose=${onClose} onConfirm=${submit}>
    <div class="template-form">
      <div class="template-form-fields">
        ${channels.length > 1
          ? html`<${SelectField} label=${t('whatsappTemplates.form.number')} value=${channelId} onChange=${setChannelId} hint=${t('whatsappTemplates.form.numberHint')}
              options=${channels.map((channel) => ({ value: channel.id, label: `${channelLabel(channel)} (${channel.settings?.verifiedName ?? channel.displayName ?? ''})` }))} />`
          : null}
        <div class="form-field-row">
          <${TextField} label=${t('whatsappTemplates.form.name')} value=${name} onInput=${(value) => setName(value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
            placeholder="convite_visita" isRequired=${true} hint=${t('whatsappTemplates.form.nameHint')} autoFocus=${true} />
          <${SelectField} label=${t('whatsappTemplates.form.category')} value=${category} onChange=${setCategory} hint=${t('whatsappTemplates.form.categoryHint')}
            options=${['UTILITY', 'MARKETING'].map((value) => ({ value, label: t(`leadCapture.templates.categories.${value}`) }))} />
        </div>
        <label class="form-field">
          <span class="form-field-label">${t('whatsappTemplates.form.language')}</span>
          <input class="text-input" value=${languageLabel(TEMPLATE_LANGUAGE)} readOnly aria-readonly="true" />
        </label>
        <${TextField} label=${t('whatsappTemplates.form.header')} value=${headerText} onInput=${setHeaderText} maxLength=${60} />
        ${headerHasVariable ? html`<${FormError} message=${t('whatsappTemplates.form.headerVariableError')} />` : null}
        <div class="template-form-body">
          <${TextField} label=${t('whatsappTemplates.form.body')} value=${bodyText} onInput=${setBodyText} multiline=${true} maxLength=${1024} isRequired=${true}
            placeholder=${t('whatsappTemplates.form.bodyPlaceholder')} hint=${t('whatsappTemplates.form.bodyHint')} />
          <${Button} title=${t('whatsappTemplates.form.insertVariable')} icon="IconBraces" variant="secondary" size="small"
            onClick=${() => setBodyText(`${bodyText}${bodyText && !bodyText.endsWith(' ') ? ' ' : ''}${nextVariableToken(bodyText)}`)} />
        </div>
        ${variables.map((variable) => html`<${TextField} key=${variable} label=${t('whatsappTemplates.form.example', { variable })} value=${examples[variable] ?? ''}
          onInput=${(value) => setExamples({ ...examples, [variable]: value })} isRequired=${true} />`)}
        <${TextField} label=${t('whatsappTemplates.form.footer')} value=${footerText} onInput=${setFooterText} maxLength=${60} />
        <${FormError} message=${error} />
      </div>
      <div class="template-form-preview">
        <span class="form-field-label">${t('whatsappTemplates.form.preview')}</span>
        <${TemplatePreviewBubble} headerText=${headerText} bodyText=${bodyText || t('whatsappTemplates.form.bodyPlaceholder')} footerText=${footerText}
          values=${examples} label=${t('whatsappTemplates.form.preview')} />
      </div>
    </div>
  <//>`;
}
